/**
 * 기업회원 › 기업회원관리 (운영자 전용) — 회사를 골라 **비밀번호만** 재설정한다.
 *
 * 시안 SSOT: `docs/attendance-round2-sample.html` 화면 L. 규칙은 제안 1 · 결정 ⑧.
 *
 * 이 화면이 일부러 **하지 않는** 것:
 *   - 이름·역할·조직·인사 정보를 고치지 않는다(회사가 직접 한다 — 8443 직원 관리·관리 콘솔).
 *   - 연락처·생일·근태를 보여 주지 않는다. 서버가 아예 보내지 않으므로 **여기서 가릴 것도 없다**.
 *   - 다른 운영자(SYSTEM_ADMIN)·봇·본인 계정은 대상이 아니다(서버가 403/400 으로 막는다).
 *
 * 하는 것 하나: **잠긴 계정을 여는 일**. 회사 ADMIN 까지 열 수 있고(그래야 잠긴 회사를 푼다),
 * 대신 사유가 필수이며 **그 사유가 본인 통지에 그대로 실린다** — 화면이 그것을 미리 말한다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import './SystemUserMaintenance.css';
import { AlertTriangle, Building2, Check, Copy, Key, Loader2, Search, ShieldCheck, Users, X } from '../../../icons';
import {
  listSystemTenants, listSystemUserCounts, listSystemUsers, resetSystemUserPassword,
  type ResetMode, type SystemMaintenanceUser, type SystemResetResult, type SystemTenant,
} from '../../../api/systemUsers';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

const ROLE_LABEL: Record<string, string> = {
  SYSTEM_ADMIN: '시스템관리자', ADMIN: '관리자', MANAGER: '매니저', STAFF: '직원',
};
const STATUS_LABEL: Record<string, string> = {
  active: '활성', invited: '초대됨', disabled: '비활성', deleted: '탈퇴', offline: '활성', online: '활성',
};
const MAIL_LABEL: Record<string, string> = {
  sent: '본인에게 메일을 보냈습니다.',
  skipped_not_configured: '메일은 나가지 않았습니다(SMTP 미설정) — 본인에게 직접 알려 주세요.',
  failed: '메일 발송에 실패했습니다 — 본인에게 직접 알려 주세요.',
};
/** 사유 하한은 서버(10자)와 같은 값이다. 화면에서 먼저 막아 400 을 왕복하지 않는다. */
const REASON_MIN = 10;

function errMessage(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
  return r?.message ?? r?.code ?? fallback;
}

export function SystemUserMaintenance() {
  const [tenants, setTenants] = useState<SystemTenant[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tenantQ, setTenantQ] = useState('');
  const [picked, setPicked] = useState<SystemTenant | null>(null);

  const [users, setUsers] = useState<SystemMaintenanceUser[]>([]);
  const [total, setTotal] = useState(0);
  const [userQ, setUserQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [target, setTarget] = useState<SystemMaintenanceUser | null>(null);
  const [mode, setMode] = useState<ResetMode>('temp_password');
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [result, setResult] = useState<SystemResetResult | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listSystemTenants(), listSystemUserCounts().catch(() => ({}))])
      .then(([items, c]) => {
        if (cancelled) return;
        setTenants(items);
        setCounts(c);
      })
      .catch((e) => { if (!cancelled) setError(errMessage(e, '회사 목록을 불러오지 못했습니다.')); });
    return () => { cancelled = true; };
  }, []);

  const loadUsers = useCallback(async (inst: SystemTenant, search: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await listSystemUsers({ institutionId: inst.id, search: search.trim() || undefined });
      setUsers(r.users);
      setTotal(r.total);
    } catch (e) {
      setUsers([]);
      setTotal(0);
      setError(errMessage(e, '사용자 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  // 검색은 디바운스 — 회사를 고른 뒤에만 돈다.
  useEffect(() => {
    if (!picked) return;
    const t = setTimeout(() => { void loadUsers(picked, userQ); }, 250);
    return () => clearTimeout(t);
  }, [picked, userQ, loadUsers]);

  const shownTenants = useMemo(() => {
    const q = tenantQ.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q));
  }, [tenants, tenantQ]);

  function openReset(u: SystemMaintenanceUser) {
    setTarget(u);
    // 사번이 없으면 임시비번을 쓸 수 없다(활성화가 이메일+사번+임시비번 3-factor).
    setMode(u.employeeNumber ? 'temp_password' : 'invite_link');
    setReason('');
    setModalError(null);
    setResult(null);
    setCopied(false);
  }

  function closeReset() {
    setTarget(null);
    setResult(null);
    setWorking(false);
  }

  async function submitReset() {
    if (!target || !picked) return;
    setWorking(true);
    setModalError(null);
    try {
      const r = await resetSystemUserPassword(target.id, {
        institutionId: picked.id,
        reason: reason.trim(),
        mode,
      });
      setResult(r);
      // 재설정하면 비밀번호가 비워진다 — 목록도 그 사실을 바로 반영한다.
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, hasPassword: false } : u)));
    } catch (e) {
      setModalError(errMessage(e, '재설정에 실패했습니다.'));
    } finally {
      setWorking(false);
    }
  }

  const secret = result?.invite.tempPassword ?? result?.invite.token ?? '';
  const reasonTooShort = reason.trim().length < REASON_MIN;

  return (
    <div className="sum">
      {/* ⚠ 2026-09-09 — 제목이 메뉴 이름과 달랐다(메뉴 「기업회원관리」 / 제목 「사용자 관리」).
          경로는 셸이 그리므로 부제에서 뺐다. */}
      <ConsoleScreenHead
        title="기업회원관리"
        desc="회사를 골라 그 회사 사용자의 비밀번호만 재설정합니다."
      />

      {error && (
        <div className="sum__banner sum__banner--err">
          <AlertTriangle size={15} /><span>{error}</span>
        </div>
      )}

      <div className="sum__body">
        <div className="sum__panel">
          <div className="sum__panel-head">
            <span><Building2 size={14} /> 회사</span>
            <span className="sum__count">{tenants.length}곳</span>
          </div>
          <div className="sum__search">
            <Search size={14} />
            <input
              value={tenantQ}
              onChange={(e) => setTenantQ(e.target.value)}
              placeholder="회사 이름 · 코드 검색"
              aria-label="회사 검색"
            />
          </div>
          <div className="sum__tenants">
            {shownTenants.length === 0 ? (
              <div className="sum__empty"><Building2 size={22} /><span>회사가 없습니다</span></div>
            ) : shownTenants.map((t) => (
              <button
                key={t.id}
                type="button"
                className="sum__tenant"
                aria-pressed={picked?.id === t.id}
                onClick={() => { setPicked(t); setUserQ(''); }}
              >
                <span className="sum__tenant-code">{t.code}</span>
                <span className="sum__tenant-name">{t.name}</span>
                <span className="sum__tenant-n">{counts[t.id] ?? '-'}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="sum__panel">
          <div className="sum__panel-head">
            <span><Users size={14} /> {picked ? `${picked.name} 사용자` : '사용자'}</span>
            <span className="sum__count">{picked ? `${total}명 · 재설정 대상` : '회사를 고르세요'}</span>
          </div>

          <div className="sum__note">
            고칠 수 있는 것은 <b>비밀번호 재설정 하나</b>입니다. 이름 · 이메일 · 사번 · 역할 · 조직 ·
            인사 정보는 <b>회사가 직접</b> 합니다. 그리고 근태 · 연락처 · 생일 같은 개인정보는
            <b> 응답에 담기지 않습니다</b> — 화면에서 가리는 것이 아니라 서버가 빼고 보냅니다.
          </div>

          {picked && (
            <div className="sum__search">
              <Search size={14} />
              <input
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                placeholder="이름 · 이메일 · 사번 검색"
                aria-label="사용자 검색"
              />
            </div>
          )}

          {!picked ? (
            <div className="sum__empty">
              <Building2 size={26} />
              <span>왼쪽에서 회사를 고르면 그 회사 사용자만 보입니다.</span>
            </div>
          ) : loading ? (
            <div className="sum__empty"><Loader2 size={22} /><span>불러오는 중…</span></div>
          ) : users.length === 0 ? (
            <div className="sum__empty"><Users size={26} /><span>조건에 맞는 사용자가 없습니다.</span></div>
          ) : (
            <table className="sum__table">
              <thead>
                <tr>
                  <th>이름</th><th>이메일</th><th>사번</th><th>역할</th><th>계정</th><th>재설정</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name || '(이름 없음)'}</td>
                    <td className="sum__mono">{u.email}</td>
                    <td className="sum__mono">{u.employeeNumber ?? '-'}</td>
                    <td>
                      <span className={`sum__tag${u.role === 'ADMIN' ? ' sum__tag--admin' : ''}`}>
                        {ROLE_LABEL[u.role] ?? u.role}
                      </span>
                    </td>
                    <td>
                      <span className={`sum__tag${u.isActive ? '' : ' sum__tag--off'}`}>
                        {u.isActive ? (STATUS_LABEL[u.status] ?? u.status) : '비활성'}
                      </span>
                      {!u.hasPassword && <span className="sum__hint"> · 비번 미설정</span>}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="sum__btn"
                        disabled={u.role === 'SYSTEM_ADMIN'}
                        title={u.role === 'SYSTEM_ADMIN' ? '운영자 계정은 이 화면에서 재설정하지 않습니다' : undefined}
                        onClick={() => openReset(u)}
                      >
                        <Key size={13} /> 비밀번호 재설정
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="sum__foot">
            <span>재설정은 사유를 남기고 본인에게 알립니다 · 근태 · 연락처는 이 화면에 오지 않습니다</span>
            <span className="sum__chip">모두 감사 로그</span>
            <span className="sum__chip">회사별로 갈라 봅니다</span>
            <span className="sum__chip">비밀번호 재설정 외에는 읽기 전용</span>
          </div>
        </div>
      </div>

      {target && (
        <div className="sum__scrim" role="dialog" aria-modal="true" aria-label="비밀번호 재설정">
          <div className="sum__modal">
            <div className="sum__modal-head">
              <span>비밀번호 재설정 — {target.name}</span>
              <button type="button" className="sum__x" onClick={closeReset} aria-label="닫기"><X size={16} /></button>
            </div>

            {result ? (
              <div className="sum__modal-body">
                <div className="sum__banner sum__banner--ok">
                  <Check size={15} />
                  <span>
                    {result.name} 님의 비밀번호를 재설정했습니다. 기존 세션은 모두 종료되었습니다.
                    <br />{MAIL_LABEL[result.notified.mail] ?? ''}
                  </span>
                </div>
                <div className="sum__field">
                  <label htmlFor="sum-secret">
                    {result.invite.kind === 'temp_password' ? '임시 비밀번호' : '초대 토큰'} — 지금 한 번만 보입니다
                  </label>
                  <div className="sum__secret">
                    <code id="sum-secret">{secret}</code>
                    <button
                      type="button"
                      className="sum__btn"
                      onClick={() => {
                        void navigator.clipboard?.writeText(secret).then(() => setCopied(true)).catch(() => setCopied(false));
                      }}
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />} 복사
                    </button>
                  </div>
                  <span className="sum__hint">
                    만료 {new Date(result.invite.expiresAt).toLocaleString('ko-KR')} ·
                    {result.invite.kind === 'temp_password'
                      ? ' 본인이 이메일 · 사번 · 임시 비밀번호로 계정을 활성화하며 새 비밀번호를 정합니다.'
                      : ' 본인이 초대 링크에서 새 비밀번호를 정합니다.'}
                  </span>
                </div>
                <div className="sum__hint">
                  이 값은 서버에 평문으로 남지 않습니다 — 창을 닫으면 다시 볼 수 없고, 다시 필요하면 재설정을 한 번 더 해야 합니다.
                </div>
              </div>
            ) : (
              <div className="sum__modal-body">
                {target.role === 'ADMIN' && (
                  <div className="sum__banner">
                    <ShieldCheck size={15} />
                    <span>
                      <b>회사 최고 권한 계정</b>입니다. 회사 관리자가 잠겼을 때 푸는 용도이며,
                      이 작업은 회사의 감사 기록에 남습니다.
                    </span>
                  </div>
                )}

                <div className="sum__field">
                  <label>대상</label>
                  <div className="sum__hint">
                    {target.email} · 사번 {target.employeeNumber ?? '없음'} · {ROLE_LABEL[target.role] ?? target.role}
                    {picked ? ` · ${picked.name}` : ''}
                  </div>
                </div>

                <div className="sum__field">
                  <label>방식</label>
                  <div className="sum__modes">
                    <label className="sum__mode">
                      <input
                        type="radio"
                        name="sum-mode"
                        checked={mode === 'temp_password'}
                        disabled={!target.employeeNumber}
                        onChange={() => setMode('temp_password')}
                      />
                      <span>
                        임시 비밀번호
                        <small>
                          {target.employeeNumber
                            ? '이메일 · 사번 · 임시 비밀번호 셋으로 본인이 계정을 활성화합니다.'
                            : '사번이 없어 쓸 수 없습니다 — 초대 링크를 사용하세요.'}
                        </small>
                      </span>
                    </label>
                    <label className="sum__mode">
                      <input
                        type="radio"
                        name="sum-mode"
                        checked={mode === 'invite_link'}
                        onChange={() => setMode('invite_link')}
                      />
                      <span>
                        초대 링크
                        <small>사번 없이도 됩니다. 토큰을 본인에게 전달하면 새 비밀번호를 정합니다.</small>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="sum__field">
                  <label htmlFor="sum-reason">사유 (필수 · {REASON_MIN}자 이상)</label>
                  <textarea
                    id="sum-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="예: 회사 관리자 계정 잠금 해제 요청(티켓 1234)"
                  />
                  <span className="sum__hint">
                    사유는 감사 기록에 남고 <b>본인에게 보내는 알림에도 그대로 표시됩니다</b> — 본인이 읽을 문장으로 적어 주세요.
                  </span>
                </div>

                {modalError && (
                  <div className="sum__banner sum__banner--err">
                    <AlertTriangle size={15} /><span>{modalError}</span>
                  </div>
                )}
              </div>
            )}

            <div className="sum__modal-foot">
              {result ? (
                <button type="button" className="sum__btn sum__btn--primary" onClick={closeReset}>닫기</button>
              ) : (
                <>
                  <button type="button" className="sum__btn" onClick={closeReset}>취소</button>
                  <button
                    type="button"
                    className="sum__btn sum__btn--primary"
                    disabled={working || reasonTooShort}
                    onClick={() => { void submitReset(); }}
                  >
                    {working ? <Loader2 size={13} /> : <Key size={13} />} 재설정
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
